import { driveCurve, midiToFreq, type Synth } from './synth';

/**
 * Named, fully synthesised sound effects. Each recipe layers the classic
 * impact recipe where it applies: a transient click (0–20 ms), a body thump
 * (60–120 Hz), an element "sweetener" and a debris/tail layer. Counter,
 * stagger and part-break each get a distinct stinger. Every play gets small
 * random pitch/timing variation (see `pitchVar`/`jitter`), and recipes use the
 * voice RNG (`s.rand`) for their own variation.
 *
 * Levels are calibrated so a single sound peaks around -6 dBFS before the
 * master bus; `gain` is the per-sound trim.
 */

export interface SfxDef {
  bus?: 'sfx' | 'ui' | 'ambient';
  /** Output trim. */
  gain?: number;
  /** 0 footsteps … 3 stingers/UI. Voices are only stolen from equal or lower priority. */
  priority?: number;
  /** Simultaneous instances of this sound (oldest is stolen beyond). */
  maxInstances?: number;
  /** Minimum seconds between two starts of this sound. */
  minInterval?: number;
  /** Reverb send 0..1. */
  reverb?: number;
  /** ± random pitch as a fraction (0.04 = ±4 %). */
  pitchVar?: number;
  /** Max random start delay in seconds. */
  jitter?: number;
  build(s: Synth): void;
}

// ------------------------------------------------------------------ shared layers

/** Element-flavoured tail added to impacts and casts. */
export function sweetener(s: Synth, element: string, level = 1): void {
  switch (element) {
    case 'fire':
      s.burst({
        color: 'pink',
        at: 0.01,
        attack: 0.02,
        decay: 0.22,
        peak: 0.22 * level,
        filter: { type: 'bandpass', freq: 1400, q: 0.9, to: 500, sweep: 0.2 },
      });
      s.grains({
        count: 3,
        at: 0.02,
        spread: 0.18,
        freq: [3000, 6000],
        decay: [0.004, 0.012],
        peak: 0.12 * level,
      });
      return;
    case 'ice':
      for (let i = 0; i < 3; i++)
        s.tone({
          type: 'triangle',
          freq: s.r(2600, 4600),
          at: 0.01 + i * s.r(0.015, 0.03),
          attack: 0.001,
          decay: 0.09,
          peak: 0.07 * level,
        });
      s.burst({
        color: 'white',
        attack: 0.004,
        decay: 0.12,
        peak: 0.08 * level,
        filter: { type: 'highpass', freq: 6000 },
      });
      return;
    case 'water':
      s.burst({
        color: 'white',
        attack: 0.004,
        decay: 0.14,
        peak: 0.16 * level,
        filter: { type: 'bandpass', freq: 1800, to: 3500, sweep: 0.1, q: 1.2 },
      });
      s.tone({
        freq: 480,
        to: 1100,
        sweep: 0.05,
        at: 0.02,
        attack: 0.002,
        decay: 0.05,
        peak: 0.08 * level,
      });
      return;
    case 'lightning':
      s.tone({
        type: 'sawtooth',
        freq: 95,
        attack: 0.001,
        decay: 0.09,
        peak: 0.12 * level,
        filter: { type: 'bandpass', freq: 1500, q: 1 },
        lfo: { rate: 40, depth: 40, target: 'freq', type: 'square' },
      });
      s.burst({
        color: 'white',
        attack: 0.0005,
        decay: 0.03,
        peak: 0.2 * level,
        filter: { type: 'highpass', freq: 4000 },
      });
      return;
    case 'holy':
      s.fm({
        freq: 1760,
        ratio: 2.5,
        index: 0.8,
        indexTo: 0,
        attack: 0.002,
        decay: 0.35,
        peak: 0.08 * level,
      });
      s.tone({ freq: 2637, at: 0.02, attack: 0.004, decay: 0.3, peak: 0.04 * level });
      return;
    case 'dark':
      for (const f of [82, 87])
        s.tone({
          type: 'sawtooth',
          freq: f,
          attack: 0.01,
          decay: 0.25,
          peak: 0.08 * level,
          filter: { type: 'lowpass', freq: 500, q: 2 },
        });
      return;
    case 'earth':
      s.grains({
        count: 4,
        at: 0.02,
        spread: 0.2,
        freq: [400, 1500],
        decay: [0.01, 0.03],
        peak: 0.2 * level,
        color: 'pink',
      });
      return;
    default:
  }
}

/** Short ascending arpeggio of bell tones (UI rewards). `notes` are MIDI numbers. */
function bellArp(
  s: Synth,
  notes: readonly number[],
  step: number,
  peak: number,
  decay = 0.6,
  at = 0,
): void {
  notes.forEach((n, i) => {
    const f = midiToFreq(n);
    s.fm({
      freq: f,
      ratio: 3.5,
      index: 0.9,
      indexTo: 0,
      at: at + i * step,
      attack: 0.002,
      decay,
      peak,
    });
    s.tone({ freq: f * 2, at: at + i * step, attack: 0.002, decay: decay * 0.5, peak: peak * 0.3 });
  });
}

/** Airy high sparkle (rewards, magic). */
function sparkle(s: Synth, at: number, dur: number, peak: number): void {
  s.burst({
    color: 'white',
    at,
    attack: dur * 0.3,
    decay: dur * 0.7,
    peak,
    filter: { type: 'highpass', freq: 6500 },
  });
  s.grains({
    count: 5,
    at,
    spread: dur,
    freq: [5000, 9000],
    decay: [0.01, 0.04],
    peak: peak * 1.5,
    q: 6,
  });
}

/** Creature voice tuning for growls, roars and deaths. */
const CREATURES: Record<string, { pitch: number; formant: number }> = {
  beast: { pitch: 1, formant: 1 },
  goblinoid: { pitch: 1.8, formant: 1.35 },
  goblin: { pitch: 1.8, formant: 1.35 },
  human: { pitch: 1.3, formant: 1.2 },
  undead: { pitch: 0.85, formant: 0.8 },
  demon: { pitch: 0.7, formant: 0.85 },
  construct: { pitch: 0.55, formant: 0.7 },
  golem: { pitch: 0.55, formant: 0.7 },
  insect: { pitch: 1.9, formant: 1.6 },
  spirit: { pitch: 1.2, formant: 1.3 },
};
const creature = (s: Synth): { pitch: number; formant: number } =>
  CREATURES[s.variant.toLowerCase()] ?? CREATURES.beast!;

/** Throat: saw source through two formant bandpasses with drive. Returns the input node. */
function throat(
  s: Synth,
  f1: number,
  f2: number,
  drive: number,
  path?: readonly (readonly [number, number])[],
): AudioNode {
  const out = s.gain(1);
  const a = s.filter('bandpass', f1, 3, s.gain(1, out));
  const b = s.filter('bandpass', f2, 4, s.gain(0.55, out));
  const body = s.filter('lowpass', 1400, 0.7, s.gain(0.35, out));
  if (path) {
    s.automate(
      a.frequency,
      0,
      f1,
      path.map(([t, k]) => [t, f1 * k] as const),
    );
    s.automate(
      b.frequency,
      0,
      f2,
      path.map(([t, k]) => [t, f2 * k] as const),
    );
  }
  const pre = s.shaper(driveCurve(drive), null);
  pre.connect(a);
  pre.connect(b);
  pre.connect(body);
  return pre;
}

// ------------------------------------------------------------------ library

export const SFX = {
  // ---------------------------------------------------------------- combat: swings & impacts
  swing_light: {
    gain: 2.41,
    maxInstances: 4,
    minInterval: 0.04,
    reverb: 0.08,
    pitchVar: 0.08,
    build(s) {
      const b = s.r(0.85, 1.2);
      s.burst({
        color: 'white',
        attack: 0.03,
        decay: 0.12,
        peak: 0.45,
        filter: {
          type: 'bandpass',
          freq: 700 * b,
          q: 1.6,
          path: [
            [0.05, 3000 * b],
            [0.15, 1300 * b],
          ],
        },
      });
      s.burst({
        color: 'pink',
        at: 0.012,
        attack: 0.02,
        decay: 0.08,
        peak: 0.16,
        filter: { type: 'highpass', freq: 3800 },
      });
    },
  },
  swing_heavy: {
    gain: 1.61,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.12,
    pitchVar: 0.06,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.07,
        decay: 0.26,
        peak: 0.8,
        filter: {
          type: 'bandpass',
          freq: 300,
          q: 1.1,
          path: [
            [0.09, 1400],
            [0.3, 450],
          ],
        },
      });
      s.tone({ freq: 120, to: 70, sweep: 0.3, attack: 0.06, decay: 0.25, peak: 0.12 });
      s.burst({
        color: 'white',
        at: 0.04,
        attack: 0.03,
        decay: 0.12,
        peak: 0.1,
        filter: { type: 'highpass', freq: 4200 },
      });
    },
  },
  impact_flesh: {
    gain: 1.17,
    maxInstances: 6,
    minInterval: 0.035,
    reverb: 0.1,
    pitchVar: 0.1,
    jitter: 0.006,
    build(s) {
      s.click(0, 0.18, 2500);
      s.thump({ freq: 170, to: 65, decay: 0.12, peak: 0.5 });
      s.tone({
        type: 'triangle',
        freq: 260,
        to: 150,
        sweep: 0.06,
        attack: 0.001,
        decay: 0.08,
        peak: 0.35,
      });
      s.burst({
        color: 'pink',
        attack: 0.002,
        decay: 0.09,
        peak: 0.6,
        filter: { type: 'bandpass', freq: s.r(700, 1000), q: 1.2 },
      });
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.03,
        peak: 0.16,
        filter: { type: 'bandpass', freq: 2600, q: 1 },
      });
      s.burst({
        color: 'brown',
        at: 0.01,
        attack: 0.005,
        decay: 0.2,
        peak: 0.22,
        filter: { type: 'lowpass', freq: 500 },
      });
      sweetener(s, s.variant, 0.8);
    },
  },
  impact_metal: {
    gain: 0.83,
    maxInstances: 5,
    minInterval: 0.04,
    reverb: 0.18,
    pitchVar: 0.08,
    jitter: 0.006,
    build(s) {
      s.click(0, 0.35, 3500);
      const lp = s.filter('lowpass', 6500, 0.7);
      s.partials({
        freq: s.r(480, 620),
        ratios: [1, 2.32, 3.87, 5.12, 6.93],
        amps: [0.5, 0.35, 0.25, 0.18, 0.1],
        decays: [0.55, 0.4, 0.3, 0.22, 0.15],
        decay: 0.55,
        attack: 0.001,
        peak: 0.42,
        spread: 8,
        dest: lp,
      });
      s.thump({ freq: 130, to: 60, decay: 0.12, peak: 0.6 });
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.07,
        peak: 0.25,
        filter: { type: 'highpass', freq: 3000 },
      });
      sweetener(s, s.variant, 0.7);
    },
  },
  impact_stone: {
    gain: 1.05,
    maxInstances: 5,
    minInterval: 0.04,
    reverb: 0.14,
    pitchVar: 0.1,
    jitter: 0.006,
    build(s) {
      s.click(0, 0.28, 1800);
      s.thump({ freq: 140, to: 55, decay: 0.16, peak: 0.6 });
      s.burst({
        color: 'brown',
        attack: 0.002,
        decay: 0.16,
        peak: 0.75,
        filter: { type: 'bandpass', freq: 600, q: 0.8 },
      });
      s.burst({
        color: 'pink',
        attack: 0.001,
        decay: 0.06,
        peak: 0.3,
        filter: { type: 'bandpass', freq: 1800, q: 1.2 },
      });
      s.grains({
        count: 4,
        at: 0.03,
        spread: 0.3,
        freq: [1500, 4000],
        decay: [0.01, 0.03],
        peak: 0.22,
        falloff: 0.6,
      });
      sweetener(s, s.variant, 0.7);
    },
  },
  crit_hit: {
    gain: 0.62,
    priority: 2,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.22,
    pitchVar: 0.03,
    build(s) {
      const f = s.r(2200, 2600);
      s.fm({
        freq: f,
        ratio: 1.41,
        index: 2.4,
        indexTo: 0.1,
        attack: 0.001,
        decay: 0.45,
        peak: 0.26,
        filter: { type: 'lowpass', freq: 9000 },
      });
      s.tone({ freq: f * 2.76, attack: 0.001, decay: 0.2, peak: 0.08 });
      s.thump({ freq: 90, to: 38, decay: 0.16, peak: 0.75 });
      s.burst({
        color: 'white',
        attack: 0.0005,
        decay: 0.03,
        peak: 0.35,
        filter: { type: 'highpass', freq: 5000 },
      });
    },
  },
  counter: {
    gain: 0.95,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.08,
    reverb: 0.4,
    pitchVar: 0.01,
    build(s) {
      // Impact + glass shatter + bright chime (E6/B6).
      s.thump({ freq: 110, to: 45, decay: 0.2, peak: 0.6 });
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.07,
        peak: 0.3,
        filter: { type: 'highpass', freq: 2500 },
      });
      const hp = s.filter('highpass', 1800, 0.7);
      for (let i = 0; i < 16; i++) {
        const at = 0.005 + s.rand() ** 1.8 * 0.4;
        s.tone({
          freq: s.r(2600, 8200),
          at,
          attack: 0.001,
          decay: s.r(0.03, 0.14),
          peak: s.r(0.05, 0.14) * (1 - at),
          dest: hp,
        });
      }
      s.burst({
        color: 'white',
        at: 0.01,
        attack: 0.005,
        decay: 0.45,
        peak: 0.1,
        filter: { type: 'bandpass', freq: 6500, q: 1.5 },
      });
      s.fm({
        freq: 1318.5,
        ratio: 3.5,
        index: 1.3,
        indexTo: 0,
        at: 0.02,
        attack: 0.002,
        decay: 1.4,
        peak: 0.2,
      });
      s.fm({
        freq: 1975.5,
        ratio: 3.5,
        index: 1.0,
        indexTo: 0,
        at: 0.06,
        attack: 0.002,
        decay: 1.2,
        peak: 0.15,
      });
      s.tone({ freq: 2637, at: 0.06, attack: 0.002, decay: 0.8, peak: 0.06 });
    },
  },
  stagger_break: {
    gain: 0.59,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.3,
    reverb: 0.45,
    pitchVar: 0.02,
    build(s) {
      // Deep gong: mallet strike, sub thump and blooming inharmonic partials.
      const f = s.r(88, 98);
      s.burst({
        color: 'pink',
        attack: 0.001,
        decay: 0.09,
        peak: 0.45,
        filter: { type: 'lowpass', freq: 1500 },
      });
      s.thump({ freq: 90, to: 45, decay: 0.35, peak: 0.45 });
      s.partials({
        freq: f * 11.3,
        ratios: [1, 1.41, 1.93],
        amps: [0.3, 0.2, 0.12],
        decays: [1.2, 0.9, 0.6],
        decay: 1.2,
        attack: 0.004,
        peak: 0.06,
        spread: 12,
      });
      const lp = s.filter('lowpass', 3200, 0.6);
      const ratios = [1, 1.47, 2.09, 2.56, 2.93, 3.52, 4.18, 5.08, 6.2];
      const amps = [0.5, 0.42, 0.35, 0.28, 0.25, 0.18, 0.14, 0.1, 0.07];
      const decays = [4.2, 3.6, 3.2, 2.6, 2.4, 2.0, 1.6, 1.3, 1.0];
      ratios.forEach((r, i) => {
        const attack = i < 3 ? 0.004 : s.r(0.08, 0.35);
        s.tone({
          freq: f * r,
          detune: s.r(-8, 8),
          attack,
          decay: decays[i]!,
          peak: amps[i]! * 0.42,
          dest: lp,
        });
        if (i < 4)
          s.tone({
            freq: f * r + 0.45,
            attack: attack + 0.05,
            decay: decays[i]! * 0.9,
            peak: amps[i]! * 0.22,
            dest: lp,
          });
      });
    },
  },
  part_break: {
    gain: 1.12,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.15,
    reverb: 0.3,
    pitchVar: 0.04,
    build(s) {
      // Crack + snap + body + bouncing debris + dust.
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.05,
        peak: 0.35,
        filter: { type: 'highpass', freq: 1400 },
      });
      s.tone({
        type: 'square',
        freq: 190,
        to: 80,
        sweep: 0.05,
        attack: 0.001,
        decay: 0.06,
        peak: 0.22,
        filter: { type: 'bandpass', freq: 900, q: 1 },
      });
      s.thump({ freq: 110, to: 45, decay: 0.28, peak: 0.45 });
      let t = 0.07;
      let gap = 0.16;
      let amp = 0.34;
      for (let i = 0; i < 12; i++) {
        s.burst({
          color: 'pink',
          at: t,
          attack: 0.001,
          decay: s.r(0.02, 0.05),
          peak: amp * s.r(0.6, 1),
          filter: { type: 'bandpass', freq: s.r(600, 3800), q: s.r(1.5, 4) },
        });
        t += gap * s.r(0.5, 1.1);
        gap *= 0.82;
        amp *= 0.86;
      }
      s.burst({
        color: 'pink',
        at: 0.04,
        attack: 0.05,
        decay: 0.8,
        peak: 0.1,
        filter: { type: 'lowpass', freq: 900 },
      });
    },
  },
  ground_slam: {
    gain: 0.85,
    priority: 2,
    maxInstances: 4,
    minInterval: 0.06,
    reverb: 0.25,
    pitchVar: 0.06,
    build(s) {
      s.click(0, 0.35, 1500);
      s.thump({ freq: 110, to: 40, decay: 0.35, peak: 0.7 });
      s.burst({
        color: 'brown',
        attack: 0.003,
        decay: 0.45,
        peak: 0.6,
        filter: { type: 'lowpass', freq: 1800, to: 250, sweep: 0.4 },
      });
      s.burst({
        color: 'pink',
        attack: 0.002,
        decay: 0.22,
        peak: 0.45,
        filter: { type: 'bandpass', freq: 500, q: 0.9 },
      });
      s.grains({
        count: 7,
        at: 0.05,
        spread: 0.5,
        freq: [500, 3000],
        decay: [0.01, 0.04],
        peak: 0.2,
        falloff: 0.7,
        color: 'pink',
      });
      s.burst({
        color: 'pink',
        at: 0.05,
        attack: 0.05,
        decay: 0.7,
        peak: 0.12,
        filter: { type: 'lowpass', freq: 700 },
      });
      sweetener(s, s.variant, 0.8);
    },
  },

  // ---------------------------------------------------------------- casts
  cast_fire: {
    gain: 1.33,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.2,
    pitchVar: 0.06,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.06,
        hold: 0.05,
        decay: 0.35,
        peak: 0.6,
        filter: {
          type: 'bandpass',
          freq: 350,
          q: 0.9,
          path: [
            [0.12, 1900],
            [0.45, 700],
          ],
        },
      });
      s.burst({
        color: 'brown',
        attack: 0.04,
        decay: 0.45,
        peak: 0.45,
        filter: { type: 'lowpass', freq: 350 },
      });
      s.grains({
        count: 7,
        at: 0.05,
        spread: 0.4,
        freq: [2500, 6000],
        decay: [0.005, 0.02],
        peak: 0.18,
        q: 2,
      });
      s.thump({ at: 0.02, freq: 90, to: 50, decay: 0.2, peak: 0.35 });
    },
  },
  cast_ice: {
    gain: 1.28,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.3,
    pitchVar: 0.04,
    build(s) {
      if (s.variant === 'water') {
        s.burst({
          color: 'pink',
          attack: 0.03,
          decay: 0.35,
          peak: 0.45,
          filter: {
            type: 'bandpass',
            freq: 600,
            q: 1,
            path: [
              [0.1, 2400],
              [0.4, 900],
            ],
          },
        });
        for (let i = 0; i < 4; i++) {
          const f = s.r(350, 700);
          s.tone({
            freq: f,
            to: f * 2.3,
            sweep: 0.05,
            at: 0.05 + i * s.r(0.04, 0.08),
            attack: 0.002,
            decay: 0.06,
            peak: 0.12,
          });
        }
        return;
      }
      const base = s.r(1500, 1800);
      [0, 3, 7, 10, 12].forEach((n, i) =>
        s.tone({
          type: 'triangle',
          freq: base * 2 ** (n / 12),
          at: i * 0.035,
          attack: 0.002,
          decay: 0.25,
          peak: 0.11,
        }),
      );
      s.burst({
        color: 'pink',
        attack: 0.02,
        decay: 0.35,
        peak: 0.12,
        filter: { type: 'highpass', freq: 5000 },
      });
      s.fm({ freq: 2400, ratio: 2.7, index: 2, indexTo: 0, attack: 0.005, decay: 0.6, peak: 0.07 });
      s.grains({
        count: 6,
        spread: 0.3,
        freq: [3000, 6000],
        decay: [0.004, 0.012],
        peak: 0.09,
        q: 6,
      });
      s.thump({ freq: 120, to: 70, decay: 0.12, peak: 0.25 });
    },
  },
  cast_lightning: {
    gain: 1.18,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.22,
    pitchVar: 0.06,
    build(s) {
      const lp = s.filter('lowpass', 7000, 0.7);
      const bp = s.filter('bandpass', 1400, 0.8, lp);
      s.tone({
        type: 'sawtooth',
        freq: s.r(70, 110),
        attack: 0.002,
        decay: 0.22,
        peak: 0.32,
        dest: s.shaper(driveCurve(3), bp),
        lfo: { rate: s.r(35, 55), depth: 60, target: 'freq', type: 'square' },
      });
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.06,
        peak: 0.26,
        filter: { type: 'highpass', freq: 2500 },
        filter2: { type: 'lowpass', freq: 9000 },
      });
      s.burst({
        color: 'white',
        at: s.r(0.04, 0.08),
        attack: 0.001,
        decay: 0.08,
        peak: 0.2,
        filter: { type: 'highpass', freq: 2000 },
        filter2: { type: 'lowpass', freq: 8000 },
      });
      s.grains({ count: 6, spread: 0.25, freq: [3000, 7000], decay: [0.003, 0.01], peak: 0.12 });
      s.thump({ freq: 100, to: 50, decay: 0.15, peak: 0.35 });
    },
  },
  cast_holy: {
    gain: 0.97,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.4,
    pitchVar: 0.02,
    build(s) {
      const base = s.r(880, 930);
      [1, 1.25, 1.5, 2].forEach((m, i) =>
        s.tone({
          type: 'triangle',
          freq: base * m,
          at: i * 0.03,
          attack: 0.04,
          decay: 0.9,
          peak: 0.09,
          lfo: { rate: 5.5, depth: 8, target: 'detune' },
        }),
      );
      s.burst({
        color: 'white',
        attack: 0.15,
        decay: 0.6,
        peak: 0.1,
        filter: { type: 'highpass', freq: 5500 },
      });
      s.fm({
        freq: base * 3,
        ratio: 3.01,
        index: 0.8,
        indexTo: 0,
        at: 0.05,
        decay: 0.7,
        peak: 0.05,
      });
      s.tone({ freq: base / 2, attack: 0.08, decay: 0.7, peak: 0.1 });
    },
  },
  cast_dark: {
    gain: 0.76,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.3,
    pitchVar: 0.05,
    build(s) {
      const lp = s.filter('lowpass', 250, 3);
      s.automate(lp.frequency, 0, 250, [
        [0.18, 1100],
        [0.6, 200],
      ]);
      s.tone({ type: 'sawtooth', freq: 140, attack: 0.18, decay: 0.45, peak: 0.28, dest: lp });
      s.tone({ type: 'sawtooth', freq: 148.4, attack: 0.18, decay: 0.45, peak: 0.28, dest: lp });
      s.tone({ freq: 55, to: 40, attack: 0.1, decay: 0.5, peak: 0.18 });
      s.burst({
        color: 'pink',
        attack: 0.2,
        decay: 0.3,
        peak: 0.16,
        filter: { type: 'bandpass', freq: 1200, q: 2.5 },
        lfo: { rate: 14, depth: 0.8 },
      });
    },
  },
  cast_arcane: {
    gain: 1.49,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.3,
    pitchVar: 0.05,
    build(s) {
      s.fm({
        freq: 420,
        to: 1500,
        sweep: 0.2,
        ratio: 2.01,
        index: 3,
        indexTo: 0.3,
        attack: 0.01,
        decay: 0.4,
        peak: 0.16,
      });
      [0, 2, 4, 6].forEach((n, i) =>
        s.tone({
          freq: 1760 * 2 ** (n / 12),
          at: 0.03 + i * 0.04,
          attack: 0.003,
          decay: 0.2,
          peak: 0.07,
        }),
      );
      s.burst({
        color: 'white',
        attack: 0.05,
        decay: 0.3,
        peak: 0.1,
        filter: { type: 'bandpass', freq: 3000, q: 2, to: 7000, sweep: 0.3 },
      });
    },
  },
  cast_earth: {
    gain: 0.84,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.2,
    pitchVar: 0.06,
    build(s) {
      s.burst({
        color: 'white',
        attack: 0.0005,
        decay: 0.04,
        peak: 0.4,
        filter: { type: 'highpass', freq: 1200 },
      });
      s.thump({ freq: 90, to: 40, decay: 0.35, peak: 0.6 });
      s.burst({
        color: 'pink',
        attack: 0.02,
        decay: 0.5,
        peak: 0.5,
        filter: { type: 'bandpass', freq: 450, q: 0.7 },
      });
      s.grains({
        count: 10,
        at: 0.03,
        spread: 0.5,
        freq: [300, 1500],
        decay: [0.015, 0.04],
        peak: 0.25,
        falloff: 0.5,
        color: 'pink',
      });
    },
  },
  charge_up: {
    gain: 0.8,
    maxInstances: 3,
    minInterval: 0.1,
    reverb: 0.2,
    pitchVar: 0.03,
    build(s) {
      const d = Math.min(2.5, Math.max(0.3, s.hint || 0.8));
      s.burst({
        color: 'pink',
        attack: d * 0.9,
        decay: 0.15,
        peak: 0.25,
        filter: { type: 'bandpass', freq: 300, q: 3, to: 2500, sweep: d },
      });
      s.tone({
        type: 'sawtooth',
        freq: 110,
        to: 330,
        sweep: d,
        attack: d,
        decay: 0.12,
        peak: 0.08,
        filter: { type: 'lowpass', freq: 400, to: 2400, sweep: d },
      });
      s.tone({ freq: 220, to: 660, sweep: d, attack: d * 0.8, decay: 0.15, peak: 0.08 });
    },
  },
  blink: {
    gain: 2.1,
    maxInstances: 3,
    minInterval: 0.05,
    reverb: 0.25,
    pitchVar: 0.05,
    build(s) {
      s.tone({ freq: 600, to: 2400, sweep: 0.12, attack: 0.005, decay: 0.15, peak: 0.16 });
      s.burst({
        color: 'white',
        attack: 0.01,
        decay: 0.12,
        peak: 0.2,
        filter: { type: 'bandpass', freq: 1500, q: 1.5, to: 7000, sweep: 0.12 },
      });
      s.grains({
        count: 4,
        at: 0.04,
        spread: 0.15,
        freq: [5000, 9000],
        decay: [0.01, 0.03],
        peak: 0.1,
        q: 6,
      });
    },
  },
  explosion: {
    gain: 0.78,
    priority: 2,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.3,
    pitchVar: 0.08,
    build(s) {
      s.click(0, 0.45, 1500);
      s.thump({ freq: 95, to: 30, decay: 0.5, peak: 0.7 });
      s.burst({
        color: 'pink',
        attack: 0.003,
        decay: 0.4,
        peak: 0.4,
        drive: 2,
        filter: { type: 'bandpass', freq: 450, q: 0.8, to: 250, sweep: 0.4 },
      });
      s.burst({
        color: 'brown',
        attack: 0.004,
        decay: 0.9,
        peak: 0.8,
        drive: 1.5,
        filter: { type: 'lowpass', freq: 2400, q: 0.5, to: 280, sweep: 0.8 },
      });
      s.burst({
        color: 'white',
        attack: 0.002,
        decay: 0.2,
        peak: 0.22,
        filter: { type: 'bandpass', freq: 1800, q: 0.7 },
      });
      s.grains({
        count: 10,
        at: 0.08,
        spread: 0.9,
        freq: [800, 3500],
        decay: [0.01, 0.04],
        peak: 0.18,
        falloff: 0.5,
        color: 'pink',
      });
      sweetener(s, s.variant, 1);
    },
  },

  // ---------------------------------------------------------------- guns & bows
  pistol: {
    gain: 1.19,
    maxInstances: 5,
    minInterval: 0.04,
    reverb: 0.15,
    pitchVar: 0.06,
    build(s) {
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.05,
        peak: 0.4,
        filter: { type: 'highpass', freq: 900 },
        filter2: { type: 'lowpass', freq: 9000 },
      });
      s.thump({ freq: 200, to: 75, decay: 0.07, peak: 0.45 });
      s.tone({ type: 'square', freq: 2800, attack: 0.0005, decay: 0.008, peak: 0.08 });
      s.burst({
        color: 'pink',
        at: 0.012,
        attack: 0.01,
        decay: 0.22,
        peak: 0.16,
        filter: { type: 'lowpass', freq: 1400 },
      });
    },
  },
  shotgun: {
    gain: 1.03,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.2,
    pitchVar: 0.05,
    build(s) {
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.08,
        peak: 0.45,
        filter: { type: 'highpass', freq: 600 },
        filter2: { type: 'lowpass', freq: 7000 },
      });
      s.thump({ freq: 150, to: 50, decay: 0.18, peak: 0.65 });
      s.burst({
        color: 'pink',
        attack: 0.002,
        decay: 0.35,
        peak: 0.5,
        filter: { type: 'bandpass', freq: 650, q: 0.7 },
      });
      s.grains({
        count: 6,
        at: 0.005,
        spread: 0.06,
        freq: [2000, 6000],
        decay: [0.004, 0.01],
        peak: 0.18,
      });
      s.burst({
        color: 'pink',
        at: 0.03,
        attack: 0.02,
        decay: 0.45,
        peak: 0.14,
        filter: { type: 'lowpass', freq: 900 },
      });
    },
  },
  rifle: {
    gain: 0.92,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.25,
    pitchVar: 0.04,
    build(s) {
      s.burst({
        color: 'white',
        attack: 0.001,
        decay: 0.035,
        peak: 0.45,
        filter: { type: 'highpass', freq: 2000 },
        filter2: { type: 'lowpass', freq: 10000 },
      });
      s.thump({ freq: 160, to: 50, decay: 0.2, peak: 0.6 });
      s.burst({
        color: 'pink',
        attack: 0.002,
        decay: 0.25,
        peak: 0.35,
        filter: { type: 'bandpass', freq: 500, q: 0.8 },
      });
      s.tone({ freq: 1900, attack: 0.001, decay: 0.12, peak: 0.06 });
      s.burst({
        color: 'pink',
        at: 0.03,
        attack: 0.05,
        decay: 0.9,
        peak: 0.18,
        filter: { type: 'lowpass', freq: 700, to: 300, sweep: 0.9 },
      });
      s.burst({
        color: 'pink',
        at: 0.16,
        attack: 0.01,
        decay: 0.3,
        peak: 0.09,
        filter: { type: 'bandpass', freq: 500, q: 1 },
      });
    },
  },
  arrow: {
    gain: 1.03,
    maxInstances: 5,
    minInterval: 0.04,
    reverb: 0.12,
    pitchVar: 0.06,
    build(s) {
      s.pluck({ freq: s.r(105, 125), peak: 0.5, kind: 'bow' });
      s.tone({ type: 'triangle', freq: 230, to: 200, attack: 0.001, decay: 0.08, peak: 0.12 });
      s.burst({
        color: 'white',
        at: 0.02,
        attack: 0.02,
        decay: 0.12,
        peak: 0.2,
        filter: { type: 'bandpass', freq: 2000, q: 1.5, to: 4200, sweep: 0.12 },
      });
    },
  },

  // ---------------------------------------------------------------- movement
  dash: {
    gain: 2.21,
    maxInstances: 3,
    minInterval: 0.08,
    reverb: 0.1,
    pitchVar: 0.06,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.04,
        decay: 0.2,
        peak: 0.6,
        filter: {
          type: 'bandpass',
          freq: 500,
          q: 1.4,
          path: [
            [0.08, 2600],
            [0.26, 900],
          ],
        },
      });
      s.burst({
        color: 'pink',
        at: 0.02,
        attack: 0.03,
        decay: 0.12,
        peak: 0.05,
        filter: { type: 'highpass', freq: 5000 },
      });
      s.thump({ freq: 90, to: 60, decay: 0.12, peak: 0.18 });
    },
  },
  jump_land: {
    gain: 1.01,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.1,
    pitchVar: 0.08,
    build(s) {
      s.thump({ freq: 130, to: 55, decay: 0.14, peak: 0.6 });
      s.tone({
        type: 'triangle',
        freq: 220,
        to: 140,
        sweep: 0.05,
        attack: 0.001,
        decay: 0.06,
        peak: 0.25,
      });
      s.burst({
        color: 'brown',
        attack: 0.003,
        decay: 0.14,
        peak: 0.5,
        filter: { type: 'bandpass', freq: 420, q: 0.8 },
      });
      s.grains({
        count: 3,
        at: 0.01,
        spread: 0.08,
        freq: [1000, 3000],
        decay: [0.005, 0.015],
        peak: 0.12,
        color: 'pink',
      });
    },
  },
  footstep_grass: {
    gain: 2.15,
    priority: 0,
    maxInstances: 2,
    minInterval: 0.12,
    reverb: 0.05,
    pitchVar: 0.12,
    jitter: 0.01,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.006,
        decay: 0.07,
        peak: 0.4,
        filter: { type: 'bandpass', freq: s.r(2200, 3600), q: 0.9 },
      });
      s.burst({
        color: 'pink',
        at: 0.01,
        attack: 0.004,
        decay: 0.05,
        peak: 0.05,
        filter: { type: 'highpass', freq: 5000 },
      });
      s.thump({ freq: 110, to: 70, decay: 0.04, peak: 0.15 });
    },
  },
  footstep_stone: {
    gain: 1.15,
    priority: 0,
    maxInstances: 2,
    minInterval: 0.12,
    reverb: 0.1,
    pitchVar: 0.12,
    jitter: 0.01,
    build(s) {
      s.tone({ freq: 200, to: 130, sweep: 0.03, attack: 0.001, decay: 0.035, peak: 0.32 });
      s.burst({
        color: 'pink',
        attack: 0.001,
        decay: 0.045,
        peak: 0.32,
        filter: { type: 'bandpass', freq: s.r(1300, 1800), q: 2 },
      });
      s.click(0, 0.12, 4000);
    },
  },

  // ---------------------------------------------------------------- support
  heal: {
    gain: 0.86,
    maxInstances: 3,
    minInterval: 0.1,
    reverb: 0.35,
    pitchVar: 0.02,
    build(s) {
      [0, 5, 9, 12].forEach((n, i) =>
        s.tone({
          freq: midiToFreq(79 + n),
          at: i * 0.06,
          attack: 0.012,
          decay: 0.6,
          peak: 0.1,
          lfo: { rate: 5, depth: 6, target: 'detune' },
        }),
      );
      s.tone({ freq: midiToFreq(67), attack: 0.1, decay: 0.7, peak: 0.1 });
      sparkle(s, 0.05, 0.6, 0.06);
    },
  },
  shield: {
    gain: 0.83,
    maxInstances: 3,
    minInterval: 0.1,
    reverb: 0.3,
    pitchVar: 0.03,
    build(s) {
      s.fm({
        freq: 220,
        ratio: 1.5,
        index: 3,
        indexTo: 0.4,
        attack: 0.03,
        decay: 0.55,
        peak: 0.26,
      });
      s.tone({
        freq: 880,
        attack: 0.01,
        decay: 0.5,
        peak: 0.07,
        lfo: { rate: 9, depth: 0.5, target: 'amp' },
      });
      s.tone({ freq: 1320, attack: 0.01, decay: 0.45, peak: 0.05 });
      s.burst({
        color: 'pink',
        attack: 0.05,
        decay: 0.35,
        peak: 0.18,
        filter: { type: 'bandpass', freq: 300, to: 3200, sweep: 0.35, q: 3 },
      });
    },
  },
  buff: {
    gain: 1.48,
    maxInstances: 3,
    minInterval: 0.1,
    reverb: 0.3,
    pitchVar: 0.02,
    build(s) {
      [72, 76, 79, 84].forEach((n, i) =>
        s.tone({
          type: 'triangle',
          freq: midiToFreq(n),
          at: i * 0.045,
          attack: 0.004,
          decay: 0.35,
          peak: 0.11,
        }),
      );
      s.burst({
        color: 'pink',
        attack: 0.12,
        decay: 0.25,
        peak: 0.18,
        filter: { type: 'bandpass', freq: 400, q: 1.5, to: 3000, sweep: 0.3 },
      });
      if (s.variant === 'party') {
        for (const n of [60, 64, 67])
          s.tone({
            type: 'triangle',
            freq: midiToFreq(n),
            at: 0.18,
            attack: 0.08,
            decay: 0.8,
            peak: 0.06,
            lfo: { rate: 5, depth: 7, target: 'detune' },
          });
        bellArp(s, [88], 0, 0.06, 0.8, 0.2);
      }
    },
  },
  revive: {
    gain: 1.08,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.3,
    reverb: 0.45,
    build(s) {
      s.choir({
        freqs: [60, 64, 67, 72].map(midiToFreq),
        attack: 0.4,
        hold: 0.4,
        decay: 1.2,
        peak: 0.05,
      });
      [72, 76, 79, 84, 88].forEach((n, i) =>
        s.tone({ freq: midiToFreq(n), at: 0.1 + i * 0.07, attack: 0.01, decay: 0.6, peak: 0.08 }),
      );
      s.tone({ freq: midiToFreq(48), attack: 0.3, decay: 1.2, peak: 0.12 });
      sparkle(s, 0.2, 1, 0.06);
    },
  },

  // ---------------------------------------------------------------- creatures
  monster_growl: {
    gain: 0.36,
    maxInstances: 3,
    minInterval: 0.2,
    reverb: 0.15,
    pitchVar: 0.08,
    build(s) {
      const c = creature(s);
      const f = 95 * c.pitch;
      const input = throat(s, 420 * c.formant, 1000 * c.formant, 2.5);
      const g = s.gain(0, input);
      const end = s.env(g.gain, { attack: 0.08, hold: 0.25, decay: 0.3, peak: 0.5 });
      const o = s.osc('sawtooth', f, 0, end, g);
      s.automate(o.frequency, 0, f, [
        [0.1, f * 1.1],
        [0.6, f * 0.8],
      ]);
      s.lfo(
        { rate: s.r(15, 25), depth: f * 0.08, target: 'freq', type: 'square' },
        0,
        end,
        o.frequency,
      );
      s.burst({
        color: 'pink',
        attack: 0.1,
        hold: 0.2,
        decay: 0.3,
        peak: 0.12,
        filter: { type: 'bandpass', freq: 900 * c.formant, q: 1 },
      });
    },
  },
  monster_roar: {
    gain: 0.42,
    priority: 2,
    maxInstances: 2,
    minInterval: 0.3,
    reverb: 0.3,
    pitchVar: 0.06,
    build(s) {
      const c = creature(s);
      const f = 60 * c.pitch;
      const input = throat(s, 320 * c.formant, 800 * c.formant, 3, [
        [0.3, 2.2],
        [1.0, 1.25],
      ]);
      const g = s.gain(0, input);
      const end = s.env(g.gain, { attack: 0.15, hold: 0.5, decay: 0.5, peak: 0.5 });
      for (const k of [1, 1.022]) {
        const o = s.osc('sawtooth', f * k, 0, end, g);
        s.automate(o.frequency, 0, f * k, [
          [0.3, f * k * 1.15],
          [1.1, f * k * 0.85],
        ]);
      }
      s.tone({ freq: f * 0.66, attack: 0.15, hold: 0.4, decay: 0.5, peak: 0.18 });
      s.burst({
        color: 'pink',
        attack: 0.15,
        hold: 0.45,
        decay: 0.5,
        peak: 0.2,
        filter: { type: 'bandpass', freq: 1000 * c.formant, q: 0.9 },
        lfo: { rate: 22, depth: 0.6 },
      });
    },
  },
  monster_death: {
    gain: 0.22,
    maxInstances: 4,
    minInterval: 0.08,
    reverb: 0.2,
    pitchVar: 0.08,
    build(s) {
      const c = creature(s);
      const f = 150 * c.pitch;
      const lp = s.filter('lowpass', 1200 * c.formant, 1.5);
      s.automate(lp.frequency, 0, 1200 * c.formant, [[0.7, 400 * c.formant]]);
      const g = s.gain(0, s.shaper(driveCurve(2), lp));
      const end = s.env(g.gain, { attack: 0.03, hold: 0.25, decay: 0.45, peak: 0.45 });
      const o = s.osc('sawtooth', f, 0, end, g);
      s.automate(o.frequency, 0, f, [[0.7, f * 0.4]]);
      s.burst({
        color: 'pink',
        attack: 0.04,
        hold: 0.2,
        decay: 0.45,
        peak: 0.12,
        filter: { type: 'bandpass', freq: 800 * c.formant, q: 1, to: 300, sweep: 0.6 },
      });
      s.thump({ at: 0.42, freq: 90, to: 40, decay: 0.2, peak: 0.55 });
      s.grains({
        count: 3,
        at: 0.44,
        spread: 0.12,
        freq: [800, 2500],
        decay: [0.01, 0.02],
        peak: 0.1,
        color: 'pink',
      });
    },
  },
  boss_roar: {
    gain: 0.57,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.6,
    reverb: 0.45,
    pitchVar: 0.03,
    build(s) {
      const dying = s.variant === 'death';
      const f = 46;
      const input = throat(s, 280, 720, 4, [
        [0.5, 2.3],
        [1.8, dying ? 0.8 : 1.3],
      ]);
      const g = s.gain(0, input);
      const end = s.env(g.gain, { attack: 0.25, hold: 1.0, decay: 1.1, peak: 0.45 });
      for (const k of [1, 1.026, 0.98]) {
        const o = s.osc('sawtooth', f * k, 0, end, g);
        s.automate(o.frequency, 0, f * k, [
          [0.5, f * k * 1.2],
          [2.2, f * k * (dying ? 0.55 : 0.9)],
        ]);
      }
      s.tone({ freq: 34, attack: 0.25, hold: 0.9, decay: 1, peak: 0.22 });
      const screech = s.filter('bandpass', 1800, 3);
      s.tone({
        type: 'sawtooth',
        freq: 420,
        path: [
          [0.6, 470],
          [2, dying ? 200 : 300],
        ],
        attack: 0.3,
        hold: 0.7,
        decay: 0.9,
        peak: 0.07,
        dest: screech,
      });
      s.burst({
        color: 'pink',
        attack: 0.25,
        hold: 0.9,
        decay: 1,
        peak: 0.28,
        filter: { type: 'bandpass', freq: 900, q: 0.8 },
        lfo: { rate: 20, depth: 0.7 },
      });
      s.burst({
        color: 'brown',
        attack: 0.3,
        hold: 0.8,
        decay: 1,
        peak: 0.18,
        filter: { type: 'lowpass', freq: 300 },
      });
    },
  },
  telegraph_warning: {
    gain: 0.96,
    priority: 2,
    maxInstances: 2,
    minInterval: 0.25,
    reverb: 0.2,
    pitchVar: 0.02,
    build(s) {
      const d = Math.min(1.6, Math.max(0.4, s.hint || 0.8));
      for (const det of [0, 1.009]) {
        s.tone({
          type: 'sawtooth',
          freq: 180 * (det || 1),
          to: 262 * (det || 1),
          sweep: d,
          attack: d * 0.7,
          decay: 0.25,
          peak: 0.13,
          filter: { type: 'lowpass', freq: 900, q: 2 },
        });
      }
      s.fm({
        freq: 1250,
        ratio: 2.4,
        index: 1.5,
        indexTo: 0,
        attack: 0.002,
        decay: 0.35,
        peak: 0.08,
      });
    },
  },
  counter_cue: {
    gain: 1.2,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.2,
    reverb: 0.3,
    pitchVar: 0.01,
    build(s) {
      // Blade glint "shing": the cue that a blue-glowing attack can be countered.
      s.burst({
        color: 'white',
        attack: 0.01,
        decay: 0.18,
        peak: 0.24,
        filter: { type: 'bandpass', freq: 2000, q: 4, to: 9000, sweep: 0.15 },
      });
      s.fm({ freq: 2637, ratio: 2, index: 1, indexTo: 0, attack: 0.002, decay: 0.5, peak: 0.14 });
      s.fm({
        freq: 3520,
        ratio: 2,
        index: 0.8,
        indexTo: 0,
        at: 0.06,
        attack: 0.002,
        decay: 0.45,
        peak: 0.1,
      });
    },
  },
  announce_boss: {
    gain: 0.71,
    priority: 3,
    maxInstances: 1,
    minInterval: 1,
    reverb: 0.45,
    build(s) {
      // Ominous low horns over a war drum.
      s.brass({ freq: 73.4, dur: 1.0, peak: 0.2, bright: 0.8 });
      s.brass({ freq: 110, at: 0.02, dur: 1.0, peak: 0.15, bright: 0.8 });
      s.brass({ freq: 103.8, at: 0.6, dur: 0.6, peak: 0.1, bright: 0.7 });
      s.thump({ freq: 120, to: 50, decay: 0.6, peak: 0.5 });
      s.burst({
        color: 'pink',
        attack: 0.002,
        decay: 0.08,
        peak: 0.35,
        filter: { type: 'lowpass', freq: 900 },
      });
      s.thump({ at: 0.5, freq: 110, to: 48, decay: 0.5, peak: 0.35 });
    },
  },
  alert: {
    gain: 1.67,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.5,
    reverb: 0.3,
    build(s) {
      const soft = s.variant === 'mechanic';
      for (const f of [311, 330])
        s.tone({
          type: 'sawtooth',
          freq: f,
          attack: 0.01,
          decay: 0.35,
          peak: soft ? 0.07 : 0.1,
          filter: { type: 'lowpass', freq: 1800, q: 1 },
        });
      s.partials({
        freq: 900,
        ratios: [1, 2.4, 3.9],
        amps: [0.5, 0.3, 0.15],
        decay: 0.4,
        peak: soft ? 0.12 : 0.18,
      });
      if (!soft) {
        for (const f of [311, 330])
          s.tone({
            type: 'sawtooth',
            freq: f,
            at: 0.18,
            attack: 0.01,
            decay: 0.3,
            peak: 0.07,
            filter: { type: 'lowpass', freq: 1500, q: 1 },
          });
        s.thump({ freq: 110, to: 55, decay: 0.2, peak: 0.3 });
      }
    },
  },
  death: {
    gain: 1.43,
    priority: 3,
    maxInstances: 1,
    minInterval: 1,
    reverb: 0.5,
    build(s) {
      const ally = s.variant === 'ally';
      const k = ally ? 0.6 : 1;
      const lp = s.filter('lowpass', 700, 0.7);
      for (const n of [50, 53, 57, 62]) {
        for (const det of [-6, 6])
          s.tone({
            type: 'sawtooth',
            freq: midiToFreq(n),
            detune: det,
            attack: 0.5,
            hold: ally ? 0.3 : 0.8,
            decay: 1.6,
            peak: 0.06 * k,
            dest: lp,
          });
      }
      s.thump({ freq: 80, to: 45, decay: 0.25, peak: 0.4 * k });
      s.thump({ at: 0.28, freq: 75, to: 42, decay: 0.25, peak: 0.3 * k });
      if (!ally) {
        s.thump({ at: 1.3, freq: 72, to: 42, decay: 0.25, peak: 0.2 });
        s.thump({ at: 1.58, freq: 70, to: 40, decay: 0.25, peak: 0.14 });
      }
      s.burst({
        color: 'pink',
        attack: 0.6,
        decay: 0.3,
        peak: 0.1 * k,
        filter: { type: 'lowpass', freq: 600 },
      });
    },
  },

  // ---------------------------------------------------------------- world
  bell_chime: {
    gain: 0.57,
    maxInstances: 4,
    minInterval: 0.05,
    reverb: 0.45,
    pitchVar: 0,
    build(s) {
      const note = s.variant
        ? Number(s.variant)
        : 84 + s.pick([0, 2, 4, 7, 9]) + (s.rand() < 0.3 ? 12 : 0);
      const f = midiToFreq(Number.isFinite(note) ? note : 84);
      s.partials({
        freq: f,
        ratios: [1, 2, 2.76, 4.07, 5.4],
        amps: [0.4, 0.2, 0.18, 0.08, 0.06],
        decays: [2.2, 1.6, 1.2, 0.7, 0.5],
        decay: 2.2,
        attack: 0.002,
        peak: 0.4,
      });
      s.click(0, 0.05, 5000);
    },
  },
  portal: {
    gain: 0.74,
    maxInstances: 2,
    minInterval: 0.3,
    reverb: 0.45,
    build(s) {
      for (const f of [220, 330, 331.5])
        s.tone({
          freq: f,
          attack: 0.3,
          hold: 0.4,
          decay: 0.7,
          peak: 0.08,
          lfo: { rate: 6, depth: 25, target: 'detune' },
        });
      s.burst({
        color: 'pink',
        attack: 0.3,
        hold: 0.3,
        decay: 0.8,
        peak: 0.25,
        filter: {
          type: 'bandpass',
          freq: 400,
          q: 6,
          path: [
            [0.5, 2400],
            [1.4, 600],
          ],
        },
        lfo: { rate: 7, depth: 0.6 },
      });
      sparkle(s, 0.2, 1, 0.05);
    },
  },
  waystone: {
    gain: 0.78,
    priority: 2,
    maxInstances: 1,
    minInterval: 0.5,
    reverb: 0.5,
    build(s) {
      // Lydian shimmer over a rising hum.
      bellArp(s, [72, 76, 78, 83, 84], 0.09, 0.08, 1.2);
      for (const f of [130.8, 196])
        s.tone({ freq: f, attack: 0.4, hold: 0.3, decay: 1.2, peak: 0.12 });
      sparkle(s, 0.3, 1.2, 0.06);
    },
  },
  pickup: {
    bus: 'ui',
    gain: 0.99,
    maxInstances: 3,
    minInterval: 0.05,
    reverb: 0.1,
    pitchVar: 0.02,
    build(s) {
      s.tone({ freq: 1318.5, attack: 0.002, decay: 0.08, peak: 0.2 });
      s.tone({ freq: 1760, at: 0.045, attack: 0.002, decay: 0.14, peak: 0.2 });
    },
  },
  loot_common: {
    gain: 1.46,
    maxInstances: 3,
    minInterval: 0.06,
    reverb: 0.15,
    pitchVar: 0.05,
    build(s) {
      s.pluck({ freq: 1046.5, peak: 0.3, kind: 'music' });
      s.tone({ freq: 500, to: 300, attack: 0.002, decay: 0.06, peak: 0.2 });
      s.click(0, 0.08, 4000);
    },
  },
  loot_rare: {
    gain: 0.71,
    priority: 2,
    maxInstances: 2,
    minInterval: 0.1,
    reverb: 0.35,
    build(s) {
      bellArp(s, [88, 92, 95], 0.07, 0.12, 0.8);
      sparkle(s, 0.05, 0.6, 0.06);
    },
  },
  loot_epic: {
    gain: 0.8,
    priority: 2,
    maxInstances: 2,
    minInterval: 0.2,
    reverb: 0.4,
    build(s) {
      bellArp(s, [85, 88, 92, 97], 0.07, 0.11, 0.9);
      s.choir({
        freqs: [61, 64, 68, 73].map(midiToFreq),
        attack: 0.3,
        hold: 0.3,
        decay: 1.0,
        peak: 0.045,
      });
      sparkle(s, 0.1, 0.9, 0.07);
    },
  },
  loot_legendary: {
    gain: 0.94,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.3,
    reverb: 0.45,
    build(s) {
      s.thump({ freq: 85, to: 40, decay: 0.5, peak: 0.4 });
      bellArp(s, [74, 78, 81, 86, 90], 0.08, 0.11, 1.1, 0.05);
      s.choir({
        freqs: [50, 62, 66, 69, 74].map(midiToFreq),
        at: 0.1,
        attack: 0.4,
        hold: 0.6,
        decay: 1.4,
        peak: 0.045,
      });
      s.fm({
        freq: midiToFreq(98),
        ratio: 3.5,
        index: 1,
        indexTo: 0,
        at: 0.45,
        attack: 0.002,
        decay: 1.4,
        peak: 0.07,
      });
      sparkle(s, 0.2, 1.6, 0.08);
    },
  },
  coin: {
    bus: 'ui',
    gain: 1.12,
    maxInstances: 3,
    minInterval: 0.05,
    reverb: 0.12,
    pitchVar: 0.04,
    build(s) {
      const n = 2 + Math.floor(s.rand() * 2);
      let t = 0;
      for (let i = 0; i < n; i++) {
        const f = s.r(2600, 3200);
        s.fm({
          freq: f,
          ratio: 3.46,
          index: 1.2,
          indexTo: 0,
          at: t,
          attack: 0.001,
          decay: 0.14,
          peak: 0.12,
        });
        s.tone({ freq: f * 1.37, at: t, attack: 0.001, decay: 0.1, peak: 0.06 });
        t += s.r(0.03, 0.065);
      }
      s.click(0, 0.06, 5000);
    },
  },
  level_up: {
    gain: 0.81,
    priority: 3,
    maxInstances: 1,
    minInterval: 1,
    reverb: 0.4,
    build(s) {
      // Brass fanfare (G4 C5 E5 → G5), choir chord and a timpani roll.
      [67, 72, 76].forEach((n, i) =>
        s.brass({ freq: midiToFreq(n), at: i * 0.09, dur: 0.08, peak: 0.13 }),
      );
      s.brass({ freq: midiToFreq(79), at: 0.27, dur: 0.9, peak: 0.15 });
      s.brass({ freq: midiToFreq(72), at: 0.27, dur: 0.9, peak: 0.1 });
      s.choir({
        freqs: [60, 64, 67, 72].map(midiToFreq),
        at: 0.25,
        attack: 0.25,
        hold: 0.7,
        decay: 1.2,
        peak: 0.05,
      });
      for (let i = 0; i < 8; i++)
        s.thump({ at: i * 0.035, freq: 110, to: 80, decay: 0.2, peak: 0.06 + i * 0.03 });
      s.thump({ at: 0.28, freq: 100, to: 50, decay: 0.5, peak: 0.35 });
      sparkle(s, 0.3, 1.4, 0.07);
    },
  },
  quest_accept: {
    bus: 'ui',
    gain: 1.06,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.3,
    reverb: 0.25,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.04,
        decay: 0.15,
        peak: 0.25,
        filter: { type: 'bandpass', freq: 800, q: 1.2, to: 2000, sweep: 0.15 },
      });
      bellArp(s, [79, 84], 0.12, 0.1, 0.7, 0.05);
    },
  },
  quest_complete: {
    bus: 'ui',
    gain: 0.82,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.5,
    reverb: 0.35,
    build(s) {
      // The leitmotif as a small fanfare: C5 G5 A5 (held) over a C chord.
      s.brass({ freq: midiToFreq(72), dur: 0.12, peak: 0.1 });
      s.brass({ freq: midiToFreq(79), at: 0.14, dur: 0.1, peak: 0.1 });
      s.brass({ freq: midiToFreq(81), at: 0.26, dur: 0.6, peak: 0.11 });
      s.brass({ freq: midiToFreq(77), at: 0.26, dur: 0.6, peak: 0.07 });
      bellArp(s, [84, 88, 91], 0.05, 0.06, 0.9, 0.26);
      s.thump({ at: 0.26, freq: 100, to: 55, decay: 0.3, peak: 0.25 });
      sparkle(s, 0.26, 0.8, 0.05);
    },
  },

  // ---------------------------------------------------------------- UI
  ui_click: {
    bus: 'ui',
    gain: 1.67,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.03,
    pitchVar: 0.03,
    build(s) {
      s.tone({ freq: 1100, to: 900, sweep: 0.02, attack: 0.001, decay: 0.03, peak: 0.25 });
      s.burst({
        color: 'white',
        attack: 0.0005,
        decay: 0.01,
        peak: 0.08,
        filter: { type: 'lowpass', freq: 3000 },
      });
    },
  },
  ui_hover: {
    bus: 'ui',
    gain: 1.67,
    priority: 1,
    maxInstances: 1,
    minInterval: 0.04,
    pitchVar: 0.02,
    build(s) {
      s.tone({ freq: 1800, attack: 0.001, decay: 0.02, peak: 0.1 });
    },
  },
  ui_open: {
    bus: 'ui',
    gain: 2.13,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.05,
    reverb: 0.05,
    pitchVar: 0.03,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.06,
        decay: 0.14,
        peak: 0.32,
        filter: { type: 'bandpass', freq: 500, q: 1.2, to: 2200, sweep: 0.15 },
      });
      s.thump({ freq: 150, to: 100, decay: 0.05, peak: 0.14 });
      s.click(0.07, 0.05, 3000);
    },
  },
  ui_close: {
    bus: 'ui',
    gain: 3.92,
    priority: 3,
    maxInstances: 2,
    minInterval: 0.05,
    reverb: 0.05,
    pitchVar: 0.03,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.04,
        decay: 0.12,
        peak: 0.28,
        filter: { type: 'bandpass', freq: 2200, q: 1.2, to: 500, sweep: 0.12 },
      });
      s.click(0.08, 0.05, 2000);
    },
  },
  ui_error: {
    bus: 'ui',
    gain: 1.89,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.15,
    build(s) {
      s.tone({
        type: 'sawtooth',
        freq: 240,
        attack: 0.004,
        decay: 0.09,
        peak: 0.16,
        filter: { type: 'lowpass', freq: 1300 },
      });
      s.tone({
        type: 'sawtooth',
        freq: 180,
        at: 0.1,
        attack: 0.004,
        decay: 0.14,
        peak: 0.16,
        filter: { type: 'lowpass', freq: 1100 },
      });
    },
  },

  // ---------------------------------------------------------------- tempering
  hone_strike: {
    gain: 0.81,
    maxInstances: 2,
    minInterval: 0.08,
    reverb: 0.3,
    pitchVar: 0.03,
    build(s) {
      s.click(0, 0.3, 3000);
      s.partials({
        freq: s.r(1300, 1400),
        ratios: [1, 1.53, 2.46, 3.12, 4.28],
        amps: [0.5, 0.35, 0.25, 0.15, 0.1],
        decays: [0.9, 0.6, 0.45, 0.3, 0.2],
        decay: 0.9,
        attack: 0.001,
        peak: 0.3,
        filter: { type: 'lowpass', freq: 8000 },
      });
      s.thump({ freq: 160, to: 70, decay: 0.1, peak: 0.6 });
      s.grains({
        count: 6,
        at: 0.005,
        spread: 0.2,
        freq: [5000, 9000],
        decay: [0.003, 0.012],
        peak: 0.12,
        q: 4,
      });
    },
  },
  hone_success: {
    gain: 1.17,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.5,
    reverb: 0.4,
    build(s) {
      // Bright rising arpeggio + burst.
      bellArp(s, [72, 76, 79, 84, 88, 91], 0.05, 0.1, 0.8);
      s.thump({ at: 0.3, freq: 100, to: 45, decay: 0.4, peak: 0.35 });
      s.burst({
        color: 'pink',
        at: 0.3,
        attack: 0.005,
        decay: 0.5,
        peak: 0.25,
        filter: { type: 'bandpass', freq: 1500, q: 0.7, to: 5000, sweep: 0.4 },
      });
      s.choir({
        freqs: [60, 64, 67, 72].map(midiToFreq),
        at: 0.3,
        attack: 0.08,
        hold: 0.4,
        decay: 1,
        peak: 0.045,
      });
      sparkle(s, 0.3, 1.1, 0.08);
    },
  },
  hone_fail: {
    gain: 1.16,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.5,
    reverb: 0.2,
    build(s) {
      // Short deflating tone: two falling notes and a dull thud.
      const lp = s.filter('lowpass', 900, 0.8);
      s.tone({
        type: 'sawtooth',
        freq: 233,
        attack: 0.02,
        hold: 0.12,
        decay: 0.15,
        peak: 0.12,
        dest: lp,
      });
      s.tone({
        type: 'sawtooth',
        freq: 220,
        to: 175,
        sweep: 0.4,
        at: 0.2,
        attack: 0.02,
        hold: 0.15,
        decay: 0.3,
        peak: 0.12,
        dest: lp,
      });
      s.thump({ at: 0.2, freq: 120, to: 65, decay: 0.15, peak: 0.25 });
    },
  },

  // ---------------------------------------------------------------- life skills & water
  gather_pick: {
    gain: 2.14,
    maxInstances: 2,
    minInterval: 0.1,
    reverb: 0.15,
    pitchVar: 0.08,
    build(s) {
      s.click(0, 0.3, 3000);
      s.partials({
        freq: s.r(2000, 2400),
        ratios: [1, 2.7],
        amps: [0.4, 0.2],
        decays: [0.12, 0.07],
        decay: 0.12,
        attack: 0.001,
        peak: 0.2,
      });
      s.burst({
        color: 'brown',
        attack: 0.002,
        decay: 0.1,
        peak: 0.45,
        filter: { type: 'bandpass', freq: 900, q: 1 },
      });
      s.grains({
        count: 4,
        at: 0.02,
        spread: 0.2,
        freq: [1500, 4000],
        decay: [0.008, 0.02],
        peak: 0.15,
        color: 'pink',
      });
    },
  },
  gather_chop: {
    gain: 1.33,
    maxInstances: 2,
    minInterval: 0.1,
    reverb: 0.15,
    pitchVar: 0.08,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.001,
        decay: 0.07,
        peak: 0.6,
        filter: { type: 'bandpass', freq: 550, q: 2.5 },
      });
      s.tone({ freq: 330, to: 290, attack: 0.001, decay: 0.1, peak: 0.3 });
      s.tone({ freq: 620, attack: 0.001, decay: 0.05, peak: 0.12 });
      s.grains({
        count: 4,
        at: 0.02,
        spread: 0.15,
        freq: [2000, 5000],
        decay: [0.004, 0.012],
        peak: 0.12,
      });
    },
  },
  gather_herb: {
    gain: 2.12,
    maxInstances: 2,
    minInterval: 0.1,
    reverb: 0.1,
    pitchVar: 0.08,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.03,
        decay: 0.2,
        peak: 0.3,
        filter: { type: 'bandpass', freq: 3000, q: 0.8 },
        lfo: { rate: 25, depth: 0.7 },
      });
      s.tone({ freq: 700, to: 350, at: 0.18, attack: 0.001, decay: 0.04, peak: 0.22 });
    },
  },
  fish_splash: {
    gain: 1.51,
    maxInstances: 2,
    minInterval: 0.1,
    reverb: 0.2,
    pitchVar: 0.08,
    build(s) {
      s.burst({
        color: 'white',
        attack: 0.003,
        decay: 0.2,
        peak: 0.4,
        filter: { type: 'bandpass', freq: 1200, q: 1, to: 3200, sweep: 0.15 },
      });
      s.tone({ freq: 350, to: 900, sweep: 0.06, at: 0.02, attack: 0.002, decay: 0.06, peak: 0.18 });
      for (let i = 0; i < 4; i++) {
        const f = s.r(1500, 3000);
        s.tone({
          freq: f,
          to: f * 1.5,
          sweep: 0.02,
          at: s.r(0.08, 0.35),
          attack: 0.001,
          decay: 0.03,
          peak: 0.06,
        });
      }
    },
  },
  fish_catch: {
    gain: 1.13,
    priority: 2,
    maxInstances: 1,
    minInterval: 0.3,
    reverb: 0.25,
    build(s) {
      s.burst({
        color: 'brown',
        attack: 0.004,
        decay: 0.4,
        peak: 0.5,
        filter: { type: 'lowpass', freq: 2500, to: 600, sweep: 0.4 },
      });
      s.burst({
        color: 'white',
        attack: 0.003,
        decay: 0.2,
        peak: 0.3,
        filter: { type: 'bandpass', freq: 2000, q: 1 },
      });
      for (let i = 0; i < 8; i++) s.click(0.1 + i * 0.03, 0.08, 3500);
      bellArp(s, [88, 93], 0.1, 0.1, 0.6, 0.35);
    },
  },
  water_splash: {
    gain: 1.05,
    maxInstances: 3,
    minInterval: 0.08,
    reverb: 0.2,
    pitchVar: 0.08,
    build(s) {
      s.burst({
        color: 'pink',
        attack: 0.005,
        decay: 0.45,
        peak: 0.55,
        filter: { type: 'lowpass', freq: 3500, to: 500, sweep: 0.4 },
      });
      s.burst({
        color: 'white',
        attack: 0.002,
        decay: 0.15,
        peak: 0.2,
        filter: { type: 'highpass', freq: 3000 },
      });
      for (let i = 0; i < 4; i++) {
        const f = s.r(300, 650);
        s.tone({
          freq: f,
          to: f * 2.2,
          sweep: 0.05,
          at: s.r(0.03, 0.25),
          attack: 0.002,
          decay: 0.05,
          peak: 0.1,
        });
      }
      for (let i = 0; i < 3; i++) {
        const f = s.r(1800, 3200);
        s.tone({
          freq: f,
          to: f * 1.4,
          sweep: 0.02,
          at: s.r(0.2, 0.5),
          attack: 0.001,
          decay: 0.03,
          peak: 0.05,
        });
      }
    },
  },
  perfect: {
    gain: 0.81,
    priority: 3,
    maxInstances: 1,
    minInterval: 0.2,
    reverb: 0.35,
    build(s) {
      s.fm({ freq: 1568, ratio: 3.5, index: 1, indexTo: 0, attack: 0.002, decay: 0.9, peak: 0.16 });
      s.fm({
        freq: 2093,
        ratio: 3.5,
        index: 0.8,
        indexTo: 0,
        at: 0.05,
        attack: 0.002,
        decay: 0.8,
        peak: 0.13,
      });
      sparkle(s, 0.02, 0.5, 0.06);
      s.thump({ freq: 120, to: 60, decay: 0.12, peak: 0.35 });
    },
  },
} satisfies Record<string, SfxDef>;

export type SfxId = keyof typeof SFX;
export const SFX_IDS = Object.keys(SFX) as SfxId[];

export function isSfxId(id: string): id is SfxId {
  return Object.prototype.hasOwnProperty.call(SFX, id);
}

export function sfxDef(id: SfxId): SfxDef {
  return SFX[id];
}
